import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { formatDurationMinutes, formatDurationRange } from '../../../../../utils/taskDuration';
import { formatBreakdownLabel } from '../utils/formatBreakdownLabel';
import './RemainingTimeBreakdownPopover.css';

const POPUP_WIDTH = 320;
const POPUP_ID_PREFIX = 'remaining-time-breakdown-';

function formatRowTime(item) {
  if (item.kind === 'unloaded-nested') {
    return 'Not loaded';
  }
  if (item.minMinutes != null && item.maxMinutes != null && item.minMinutes !== item.maxMinutes) {
    return formatDurationRange(item.minMinutes, item.maxMinutes);
  }
  if (item.minutes != null && item.minutes > 0) {
    return formatDurationMinutes(item.minutes);
  }
  return 'No est. time';
}

/**
 * Clickable remaining-time segment with a mission breakdown popup.
 */
const RemainingTimeBreakdownPopover = ({
  remainingMinutes = 0,
  remainingMinMinutes,
  missingEstCount = 0,
  breakdown = [],
  label,
  disabled = false,
}) => {
  const minMinutes = remainingMinMinutes == null ? remainingMinutes : remainingMinMinutes;
  const hasRouteRange = minMinutes !== remainingMinutes;
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const popupId = useRef(`${POPUP_ID_PREFIX}${Math.random().toString(36).slice(2, 9)}`).current;

  const close = useCallback(() => {
    setIsOpen(false);
    setMenuPosition(null);
  }, []);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    if (disabled) return;

    if (isOpen) {
      close();
      return;
    }

    window.dispatchEvent(new CustomEvent('closeTableDropdowns', { detail: { exceptId: popupId } }));

    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const estimatedHeight = Math.min(360, 120 + breakdown.length * 34);

    let top = rect.bottom + 4;
    let left = rect.left;

    if (top + estimatedHeight > viewportHeight && rect.top > estimatedHeight) {
      top = rect.top - estimatedHeight - 4;
    }
    if (left + POPUP_WIDTH > viewportWidth) {
      left = viewportWidth - POPUP_WIDTH - 8;
    }
    if (left < 8) left = 8;

    setMenuPosition({ top, left });
    setIsOpen(true);
  }, [breakdown.length, close, disabled, isOpen, popupId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (e) => {
      if (
        triggerRef.current?.contains(e.target)
        || menuRef.current?.contains(e.target)
      ) {
        return;
      }
      close();
    };

    const handleCloseOthers = (e) => {
      if (e.detail?.exceptId !== popupId) close();
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('closeTableDropdowns', handleCloseOthers);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('closeTableDropdowns', handleCloseOthers);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [close, isOpen, popupId]);

  const popup = isOpen && menuPosition ? createPortal(
    <div
      ref={menuRef}
      className="remaining-time-breakdown__menu"
      style={{ top: menuPosition.top, left: menuPosition.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="remaining-time-breakdown__header">Time remaining</div>

      {breakdown.length === 0 ? (
        <div className="remaining-time-breakdown__empty">
          No incomplete missions with estimated time.
        </div>
      ) : (
        <div className="remaining-time-breakdown__list">
          {breakdown.map((item) => {
            const missing = item.minutes == null || item.minutes <= 0;
            return (
              <div key={item.id} className="remaining-time-breakdown__row">
                <span className="remaining-time-breakdown__name">
                  {formatBreakdownLabel(item)}
                  {item.conditional && (
                    <span className="remaining-time-breakdown__route-tag">some routes</span>
                  )}
                </span>
                <span
                  className={`remaining-time-breakdown__time${missing ? ' remaining-time-breakdown__time--missing' : ''}`}
                >
                  {formatRowTime(item)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="remaining-time-breakdown__footer">
        <div className="remaining-time-breakdown__total-row">
          <span>Total</span>
          <span className="remaining-time-breakdown__total-value">
            {formatDurationRange(minMinutes, remainingMinutes)}
          </span>
        </div>
        {hasRouteRange && (
          <div className="remaining-time-breakdown__note">
            Fastest to longest route through open choices
          </div>
        )}
        {missingEstCount > 0 && (
          <div className="remaining-time-breakdown__note">
            {missingEstCount} {missingEstCount === 1 ? 'mission' : 'missions'} without est. time
          </div>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`remaining-time-breakdown__trigger${isOpen ? ' remaining-time-breakdown__trigger--open' : ''}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {label}
      </button>
      {popup}
    </>
  );
};

export default RemainingTimeBreakdownPopover;
